/**
 * D3 — Outbound Voice attestation modal.
 *
 * Surfaces the canonical attestation text (loaded from the backend's
 * /api/admin/outbound-voice/attestation endpoint, which carries the
 * SHA-locked text + current attestation status). Blocking on first
 * navigation to any voice surface when the tenant hasn't yet attested.
 *
 * Per v3 invariant 6 (Phase 2 attestation gate): the text is platform-
 * owned + SHA-locked at CI time. Customer never paraphrases. The
 * "Update available" pill appears when the GET response's SHA differs
 * from the stored snapshot — prompts re-attestation against new
 * wording.
 */
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { COLORS } from "../../theme/colors";
import { Button, Badge, Modal } from "../../components/ui";
import { outboundVoiceApi } from "../../api/client";

export default function OutboundVoiceAttestationModal({ open, onClose, onAttested }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const s = await outboundVoiceApi.attestationStatus();
        if (!cancelled) setStatus(s);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const handleAttest = async () => {
    if (!status?.current_text_sha256) return;
    setSubmitting(true);
    setError(null);
    try {
      await outboundVoiceApi.attest(status.current_text_sha256);
      const updated = await outboundVoiceApi.attestationStatus();
      setStatus(updated);
      if (onAttested) onAttested(updated);
    } catch (e) {
      setError(e.message || "Attestation failed — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  const showUpdatePill =
    status?.attested && status?.attestation_text_snapshot &&
    status?.current_text && status.attestation_text_snapshot !== status.current_text;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Outbound voice — tenant attestation"
      width={640}
      footer={
        status?.attested && !showUpdatePill ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose} variant="ghost">Cancel</Button>
            <Button
              onClick={handleAttest}
              disabled={submitting || loading || !status?.current_text_sha256}
            >
              {submitting ? "Attesting…" : status?.attested ? "Re-attest" : "I attest"}
            </Button>
          </>
        )
      }
    >
      {loading && <div style={{ color: COLORS.textMuted }}>Loading attestation status…</div>}
      {error && (
        <div style={{ color: COLORS.pink, marginBottom: 12, fontSize: 13 }}>
          {error}
        </div>
      )}

      {status && (
        <>
          {status.attested && !showUpdatePill && (
            <div style={{ marginBottom: 16 }}>
              <Badge color={COLORS.green}>Attested</Badge>
              <div style={{ marginTop: 8, fontSize: 13, color: COLORS.textMuted }}>
                Attested at {status.attested_at} by {status.attested_by_user_email || status.attested_by_user_id}.
              </div>
            </div>
          )}

          {showUpdatePill && (
            <div style={{ marginBottom: 12 }}>
              <Badge color={COLORS.orange}>Update available</Badge>
              <div style={{ marginTop: 6, fontSize: 13, color: COLORS.textMuted }}>
                The attestation wording has been updated since you last attested.
                Please review the new wording below and re-attest.
              </div>
            </div>
          )}

          <div style={{ marginBottom: 8, fontWeight: 600, fontSize: 13 }}>
            Current attestation text
          </div>
          <div
            style={{
              padding: "14px 16px",
              border: `1px solid ${COLORS.border}`,
              background: COLORS.bg,
              borderRadius: 8,
              fontSize: 13,
              lineHeight: 1.5,
              maxHeight: 280,
              overflowY: "auto",
              whiteSpace: "pre-wrap",
              fontFamily: "Inter, system-ui, sans-serif",
            }}
          >
            {status.current_text}
          </div>

          <div style={{ marginTop: 12, fontSize: 11, color: COLORS.textMuted }}>
            Clicking "{status.attested ? "Re-attest" : "I attest"}" records your
            user ID + the SHA-256 of this attestation text in the audit log.
            Required before any outbound voice campaign can dispatch.
            {/* D12 — SHA-256 was previously rendered verbatim as a
                64-char hex string, which (a) cluttered the modal +
                (b) read like a security artifact the operator might
                think they need to copy. Tuck under a <details>; the
                hash is still visible to anyone who wants to verify
                + is logged into the audit row regardless. */}
            <details style={{ marginTop: 6 }}>
              <summary style={{ cursor: "pointer", color: COLORS.textMuted }}>
                Show audit hash (SHA-256)
              </summary>
              <code style={{ display: "block", marginTop: 4, fontSize: 10, wordBreak: "break-all" }}>
                {status.current_text_sha256}
              </code>
            </details>
          </div>
        </>
      )}
    </Modal>
  );
}
